"use client";

import React, {
  CSSProperties,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { spotlight_mobile } from "@/generated";
import { detectIOS, parsePx } from "@/lib/util";
import Image from "next/image";

interface LoaderProps {
  onClick?: () => void;
  spotlight: spotlight_mobile;
  onView?: () => void;
}

const LOG = "[AdBanner]";

let cachedSafeAreaBottomPx: number | null = null;

const readSafeAreaBottomPx = () => {
  if (cachedSafeAreaBottomPx !== null) {
    return cachedSafeAreaBottomPx;
  }

  const rawSafeArea = getComputedStyle(
    document.documentElement,
  ).getPropertyValue("--app-safe-area-bottom");
  const parsedSafeArea = parsePx(rawSafeArea);

  if (parsedSafeArea || rawSafeArea.trim() === "0px") {
    cachedSafeAreaBottomPx = parsedSafeArea;
    return parsedSafeArea;
  }

  const probe = document.createElement("div");
  probe.style.position = "fixed";
  probe.style.visibility = "hidden";
  probe.style.pointerEvents = "none";
  probe.style.height = "0";
  probe.style.paddingBottom = "env(safe-area-inset-bottom, 0px)";
  document.body.appendChild(probe);
  const resolvedSafeArea = parsePx(getComputedStyle(probe).paddingBottom);
  probe.remove();

  cachedSafeAreaBottomPx = resolvedSafeArea;
  return resolvedSafeArea;
};

const subscribeNoop = () => () => {};

const AdBanner: React.FC<LoaderProps> = (props) => {
  const { onClick, spotlight, onView } = props;
  const isIos = useSyncExternalStore(subscribeNoop, detectIOS, () => false);
  const isClient = useSyncExternalStore(
    subscribeNoop,
    () => true,
    () => false,
  );
  const [reloadKey, setReloadKey] = useState(0);
  const [bottomCorrectionPx, setBottomCorrectionPx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomCorrectionRef = useRef(0);

  console.log(LOG, "render", {
    isIos,
    reloadKey,
    pictureUrl: spotlight?.picture_url,
    ua: typeof navigator !== "undefined" ? navigator.userAgent : "ssr",
  });

  useEffect(() => {
    return () => {
      document.documentElement.style.setProperty(
        "--app-ad-banner-fixed-correction",
        "0px",
      );
      document.documentElement.style.setProperty(
        "--app-bottom-banner-height",
        "80px",
      );
    };
  }, []);

  // iOS WebKit fails to repaint `position: fixed` elements after a navigation
  // until a scroll happens. Hide+show to force a fresh layout, then nudge the
  // scroll position to mimic the gesture that actually evicts the stale
  // compositor snapshot. iOS-only so Android isn't affected.
  useEffect(() => {
    const updateBannerHeight = () => {
      const el = containerRef.current;
      if (!el) return;
      const height = Math.round(el.getBoundingClientRect().height);
      if (height <= 0) return;
      document.documentElement.style.setProperty(
        "--app-bottom-banner-height",
        `${height}px`,
      );
    };

    const correctBottomPosition = () => {
      if (!isIos) return;
      const el = containerRef.current;
      if (!el) return;
      updateBannerHeight();

      const style = getComputedStyle(document.documentElement);
      const expectedBottom =
        parsePx(style.getPropertyValue("--app-native-tab-bar-height")) +
        readSafeAreaBottomPx();
      const rect = el.getBoundingClientRect();
      const actualBottom = window.innerHeight - rect.bottom;
      const delta = expectedBottom - actualBottom;

      if (Math.abs(delta) <= 1) return;

      const current = bottomCorrectionRef.current;
      const next = Math.round(Math.max(-160, Math.min(160, current + delta)));
      if (Math.abs(next - current) <= 1) return;

      bottomCorrectionRef.current = next;
      document.documentElement.style.setProperty(
        "--app-ad-banner-fixed-correction",
        `${next}px`,
      );
      setBottomCorrectionPx(next);
    };

    const forceRepaint = () => {
      const el = containerRef.current;
      if (!el) return;
      if (!isIos) return;
      console.log(LOG, "forceRepaint");
      const prevDisplay = el.style.display;
      el.style.display = "none";
      void el.offsetHeight;
      el.style.display = prevDisplay;
      const y = window.scrollY;
      window.scrollTo(0, y + 1);
      window.scrollTo(0, y);
      updateBannerHeight();
      requestAnimationFrame(correctBottomPosition);
    };

    // Covers SPA back navigation where the component is freshly rendered but
    // the iOS compositor hasn't promoted the layer yet.
    const raf = requestAnimationFrame(() => {
      forceRepaint();
      correctBottomPosition();
    });

    const onPageShow = (event: PageTransitionEvent) => {
      console.log(LOG, "pageshow", { persisted: event.persisted });
      if (event.persisted) {
        setReloadKey((k) => k + 1);
      }
      forceRepaint();
      requestAnimationFrame(correctBottomPosition);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        console.log(LOG, "visibilitychange visible");
        forceRepaint();
      }
      forceRepaint();
      requestAnimationFrame(correctBottomPosition);
    };
    updateBannerHeight();
    correctBottomPosition();
    const intervalId = window.setInterval(() => {
      updateBannerHeight();
      correctBottomPosition();
    }, 500);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("resize", updateBannerHeight);
    window.addEventListener("resize", correctBottomPosition);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelAnimationFrame(raf);
      window.clearInterval(intervalId);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("resize", updateBannerHeight);
      window.removeEventListener("resize", correctBottomPosition);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isIos]);

  if (!isClient) return null;

  const fixedStyle: CSSProperties = {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: "var(--app-corrected-ad-banner-bottom-offset)",
    zIndex: 30,
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    maxHeight: 80,
    overflow: "hidden",
    ...(isIos ? { transform: "translateZ(0)", willChange: "transform" } : {}),
  };

  return createPortal(
    <div
      ref={containerRef}
      data-ad-banner="true"
      data-bottom-correction={Math.round(bottomCorrectionPx)}
      style={fixedStyle}
      onClick={() => {
        onClick?.();
      }}
    >
      <Image
        key={isIos ? reloadKey : undefined}
        src={spotlight.picture_url}
        alt="Spotlight Banner"
        width={375}
        height={80}
        priority
        className="object-cover w-full  max-h-20"
        style={{
          width: "100%",
          height: 80,
          maxHeight: 80,
          objectFit: "cover",
          display: "block",
        }}
        onLoad={(e) => {
          const img = e.currentTarget as HTMLImageElement;
          console.log(LOG, "onLoad", {
            reloadKey,
            complete: img.complete,
            naturalWidth: img.naturalWidth,
            naturalHeight: img.naturalHeight,
            currentSrc: img.currentSrc,
          });
          onView?.();
        }}
        onError={(e) => {
          const img = e.currentTarget as HTMLImageElement;
          console.error(LOG, "onError", {
            reloadKey,
            src: img.src,
            currentSrc: img.currentSrc,
          });
        }}
      />
    </div>,
    document.body,
  );
};

export default AdBanner;
